import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyTierThresholds, { getTierThreshold } from 'loyalty/selectors/getLoyaltyTierThresholds'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import Clickable from 'components/Common/Clickable'

const CardContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: ${rem(8)};
  overflow: hidden;
  box-shadow:
    0 ${rem(1)} ${rem(1.5)} rgba(0, 0, 0, 0.07),
    0 ${rem(1.5)} ${rem(5)} rgba(0, 0, 0, 0.05);
`

const CardHeader = styled.div`
  padding: ${rem(10)} ${rem(12)};
  ${tierGradientBackgroundStyle}
`

const HeaderText = styled.span`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(14)};
  font-weight: ${props => props.theme.font.primary.weight.bold};
  line-height: ${rem(18)};
  ${tierNormalContrastColorStyle}
`

const CardBody = styled.div`
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  padding: ${rem(16)};
`

const HeaderToggle = styled.div`
  display: inline-flex;
  align-items: stretch;
  background: rgba(0, 0, 0, 0.15);
  border-radius: ${rem(5)};
  padding: ${rem(2)};
  gap: ${rem(2)};
`

const HeaderToggleOption = styled(Clickable)`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(12)};
  line-height: ${rem(16)};
  padding: ${rem(4)} ${rem(8)};
  border-radius: ${rem(3)};
  transition: background 150ms, opacity 150ms;
  ${tierNormalContrastColorStyle}

  &.is-active {
    font-weight: ${props => props.theme.font.primary.weight.bold};
    background: rgba(255, 255, 255, 0.3);
    opacity: 1;
  }

  &.is-inactive {
    font-weight: ${props => props.theme.font.primary.weight.regular};
    opacity: 0.7;
  }
`

const BodyTextStyled = styled.p`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(12)};
  font-weight: ${props => props.theme.font.primary.weight.regular};
  line-height: ${rem(16)};
  /* Using bronze contrast instead of neural one to get the same color as the figma design */
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  margin: 0;
`

const BoldText = styled.span`
  font-weight: ${props => props.theme.font.primary.weight.superBold};
`

interface Props {
  /**
   * The tier to display (next tier for unlock, current tier for retain)
   */
  tier: App.LoyaltyTier
  /**
   * Whether this is for retaining current tier (true) or unlocking next tier (false)
   */
  isRetain?: boolean
  creditsRemaining: number
  nightsRemaining: number
  /**
   * The end of the member's current membership year. Used as the "by" date in unlock mode.
   */
  membershipYearEndDate?: string
  /**
   * The date the member's tier is reviewed. Used as the "by" date in retain mode.
   */
  tierReviewDate?: string
  showToggle?: boolean
  showUpgradeView?: boolean
  onToggleView?: (showUpgrade: boolean) => void
  currentTierName?: string
  nextTierName?: string
  /**
   * For retain mode only: which membership year contains the tier review.
   * - `'current-year'` (default): tier review at end of current membership year. Show calculated remaining (bolded).
   * - `'next-year'`: member upgraded mid-year and the tier review is in the *next* membership year. Show absolute SC/Nights requirements (not bolded).
   */
  retainVariant?: 'current-year' | 'next-year'
}

function LoyaltyDrawerTierCard({
  tier,
  isRetain = false,
  creditsRemaining,
  nightsRemaining,
  membershipYearEndDate,
  tierReviewDate,
  showToggle = false,
  showUpgradeView = false,
  onToggleView,
  currentTierName,
  nextTierName,
  retainVariant = 'current-year',
}: Props) {
  const intl = useIntl()
  const tierThresholds = useAppSelector(getLoyaltyTierThresholds)
  const tierName = getLoyaltyTierDisplayName(tier)

  const hasCredits = creditsRemaining > 0
  const hasNights = nightsRemaining > 0

  const toRetainText = intl.formatMessage(
    {
      id: 'zvrL6f2WsFsW2a8dOo7t',
      defaultMessage: 'To retain {tierName}',
      description: 'Header text for tier retention requirement in the loyalty section',
    },
    { tierName },
  )
  const toUnlockText = intl.formatMessage(
    {
      id: 'LPYDBrvhK439rFvswhrv',
      defaultMessage: 'To unlock {tierName}',
      description: 'Header text for tier unlock requirement in the loyalty program section',
    },
    { tierName },
  )
  const headerText = isRetain ? toRetainText : toUnlockText

  const expiryDate = isRetain ? tierReviewDate : membershipYearEndDate
  const formattedExpiryDate = expiryDate ? <FormatDateTime value={expiryDate} format="dmy-casual-short" /> : undefined
  const boldTag = (chunks: React.ReactNode) => <BoldText>{chunks}</BoldText>
  const formatStatusCreditsCountLabel = (creditsCount: number) => intl.formatMessage(
    {
      id: 'T9LU_Xq6rIJ_qkNpEncI',
      defaultMessage: '{creditsCount, plural, one {# Status Credit} other {# Status Credits}}',
      description: 'Status credit count needed for a loyalty tier requirement',
    },
    { creditsCount },
  )
  const formatNightCountLabel = (nightsCount: number) => intl.formatMessage(
    {
      id: 'YWa6TCcIYUF7Ap3khckB',
      defaultMessage: '{nightsCount, plural, one {# night} other {# nights}}',
      description: 'Night count label needed for a loyalty tier requirement',
    },
    { nightsCount },
  )
  const formatCapitalNightCountLabel = (nightsCount: number) => intl.formatMessage(
    {
      id: '1JVKx9BBRzNbHy-a13Yi',
      defaultMessage: '{nightsCount, plural, one {# Night} other {# Nights}}',
      description: 'Capitalised night count needed for a loyalty tier requirement',
    },
    { nightsCount },
  )

  let bodyText: React.ReactNode
  if (isRetain && retainVariant === 'next-year') {
    const absoluteThreshold = tierThresholds ? getTierThreshold(tierThresholds, tier) : undefined
    const absoluteCredits = absoluteThreshold?.statusCredits ?? 0
    const absoluteNights = absoluteThreshold?.nights ?? 0
    const creditsCountLabel = formatStatusCreditsCountLabel(absoluteCredits)
    const nightsCountLabel = formatCapitalNightCountLabel(absoluteNights)
    if (expiryDate) {
      bodyText = <FormattedMessage
        id="ZulwdDbJiE7PSbGpKgMG"
        defaultMessage="Within your next membership year, you will need {creditsCountLabel} and {nightsCountLabel} by {expiryDate}."
        description="Drawer card retain next-year variant: absolute requirements with tier review date for upgraded members whose review falls in the following membership year"
        values={{ creditsCountLabel, nightsCountLabel, expiryDate: formattedExpiryDate }}
      />
    } else {
      bodyText = <FormattedMessage
        id="fnBx19LRSWhV4VhVX6nY"
        defaultMessage="Within your next membership year, you will need {creditsCountLabel} and {nightsCountLabel}."
        description="Drawer card retain next-year variant: absolute requirements without expiry for upgraded members"
        values={{ creditsCountLabel, nightsCountLabel }}
      />
    }
  } else if (hasCredits && hasNights && expiryDate) {
    const creditsCountLabel = formatStatusCreditsCountLabel(creditsRemaining)
    const nightsCountLabel = formatNightCountLabel(nightsRemaining)
    bodyText = <FormattedMessage
      id="0t2PyHKHViw4AYqt0mzy"
      defaultMessage="You need <bold>{creditsCountLabel}</bold> and <bold>{nightsCountLabel}</bold> by {expiryDate}."
      description="Drawer card: credits and nights needed with expiry date"
      values={{ creditsCountLabel, nightsCountLabel, expiryDate: formattedExpiryDate, bold: boldTag }}
    />
  } else if (hasCredits && hasNights) {
    const creditsCountLabel = formatStatusCreditsCountLabel(creditsRemaining)
    const nightsCountLabel = formatNightCountLabel(nightsRemaining)
    bodyText = <FormattedMessage
      id="9kXnV3qsLS04Cto5_hI9"
      defaultMessage="You need <bold>{creditsCountLabel}</bold> and <bold>{nightsCountLabel}</bold>."
      description="Drawer card: credits and nights needed, no expiry in the loyalty program section"
      values={{ creditsCountLabel, nightsCountLabel, bold: boldTag }}
    />
  } else if (hasCredits && expiryDate) {
    const creditsCountLabel = formatStatusCreditsCountLabel(creditsRemaining)
    bodyText = <FormattedMessage
      id="pzdsZatig0holpi2DCQr"
      defaultMessage="You need <bold>{creditsCountLabel}</bold> by {expiryDate}."
      description="Drawer card: credits needed with expiry date in the loyalty program section"
      values={{ creditsCountLabel, expiryDate: formattedExpiryDate, bold: boldTag }}
    />
  } else if (hasCredits) {
    const creditsCountLabel = formatStatusCreditsCountLabel(creditsRemaining)
    bodyText = <FormattedMessage
      id="yQYoVUB2L7VlkUIfQsee"
      defaultMessage="You need <bold>{creditsCountLabel}</bold>."
      description="Drawer card: credits needed, no expiry in the loyalty program section"
      values={{ creditsCountLabel, bold: boldTag }}
    />
  } else if (expiryDate) {
    const nightsCountLabel = formatNightCountLabel(nightsRemaining)
    bodyText = <FormattedMessage
      id="66XRkqfhi8oCi4AUoUSQ"
      defaultMessage="You need <bold>{nightsCountLabel}</bold> by {expiryDate}."
      description="Drawer card: nights needed with expiry date in the loyalty program section"
      values={{ nightsCountLabel, expiryDate: formattedExpiryDate, bold: boldTag }}
    />
  } else {
    const nightsCountLabel = formatNightCountLabel(nightsRemaining)
    bodyText = <FormattedMessage
      id="ZjSFfULZ8FowbXH6JLlP"
      defaultMessage="You need <bold>{nightsCountLabel}</bold>."
      description="Drawer card: nights needed, no expiry in the loyalty program section"
      values={{ nightsCountLabel, bold: boldTag }}
    />
  }

  return (
    <CardContainer>
      <CardHeader className={`tier-${tier}`}>
        {showToggle && currentTierName && nextTierName && (
          <HeaderToggle>
            <HeaderToggleOption
              className={`tier-${tier} ${!showUpgradeView ? 'is-active' : 'is-inactive'}`}
              onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); onToggleView?.(false) }}
            >
              {intl.formatMessage(
                { id: '9ZN5FHO5pQctKoGZ26Aw', defaultMessage: 'Retain {tierName}', description: 'Toggle option to view current tier retention requirements' },
                { tierName: currentTierName },
              )}
            </HeaderToggleOption>
            <HeaderToggleOption
              className={`tier-${tier} ${showUpgradeView ? 'is-active' : 'is-inactive'}`}
              onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); onToggleView?.(true) }}
            >
              {intl.formatMessage(
                { id: 'NilqMdlNHrDTNigNu1IR', defaultMessage: 'Unlock {tierName}', description: 'Toggle option to view next tier upgrade requirements' },
                { tierName: nextTierName },
              )}
            </HeaderToggleOption>
          </HeaderToggle>
        )}
        {!(showToggle && currentTierName && nextTierName) && (
          <HeaderText className={`tier-${tier}`}>
            {headerText}
          </HeaderText>
        )}
      </CardHeader>
      <CardBody>
        <BodyTextStyled>
          {bodyText}
        </BodyTextStyled>
      </CardBody>
    </CardContainer>
  )
}

export default LoyaltyDrawerTierCard
